import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";
import { SELLING_TIPS, SELLING_TIP_ORDER } from "@/data/sellingTipsContent";

const SITE = "https://lagooncityrealestate.ca";
const TIPS = SELLING_TIP_ORDER.map((slug) => ({ slug, title: SELLING_TIPS[slug].title, img: SELLING_TIPS[slug].img }));

export default function SellingTips() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const canonical = `${SITE}/selling-tips`;
  const title = "Selling Tips — Your Guide to Selling a Home in Lagoon City | Joselle Alves";
  const desc = "Everything you need to sell with confidence in Lagoon City: a 10-step selling guide, pricing, preparation and listing checklists, and more — from broker Joselle Alves.";

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
      </Helmet>
      <PageHeader />
      <section className="relative">
        <div className="absolute inset-0">
          <img src="/assets/community/lagoon-city-main.webp" alt="Aerial view of Lagoon City waterfront homes" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0E] via-[#0D0B0E]/85 to-[#0D0B0E]/60" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-16 sm:pt-32 sm:pb-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#F2A7BA] mb-4">Selling Tips</p>
            <h1 data-testid="selling-tips-title" className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
              Sell your Lagoon City home for <em className="italic text-[#E890A7]">top dollar</em>
            </h1>
            <p className="mt-6 max-w-2xl text-slate-300 font-light text-lg">
              Pricing, preparation, marketing and negotiation — the guides and checklists that help you sell smoothly and for the best price, from Joselle.
            </p>
          </motion.div>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="selling-tips-grid">
          {TIPS.map((t, i) => (
            <motion.div key={t.slug} data-testid={`selling-tip-${t.slug}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: i * 0.05 }}>
              <Link to={`/selling-tips/${t.slug}`} className="group relative flex flex-col rounded-3xl border border-[#E890A7]/15 bg-[#151218]/70 p-6 hover:border-[#E890A7]/45 hover:-translate-y-1 transition-all duration-300 h-full">
                <div className="h-44 flex items-center justify-center overflow-hidden">
                  <img src={t.img} alt={t.title} className="h-full w-auto object-contain transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="mt-5 flex items-start justify-between gap-3">
                  <h2 className="font-display text-xl leading-snug">{t.title}</h2>
                  <ArrowUpRight size={18} className="mt-1 shrink-0 text-[#E890A7]/70 group-hover:text-[#F2A7BA] transition-all duration-300" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-[2rem] border border-[#E890A7]/25 bg-gradient-to-br from-[#1E1A23] to-[#151218] p-8 sm:p-12">
          <h2 className="font-display font-light text-3xl sm:text-4xl leading-tight max-w-2xl">Thinking of selling in Lagoon City?</h2>
          <p className="mt-4 max-w-2xl text-slate-400 font-light">Start with a free, no-obligation home evaluation — or just ask Joselle a question.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link data-testid="selling-tips-cta-eval" to="/home-evaluation" className="rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 flex items-center gap-2 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-all duration-300">
              Free home evaluation <ArrowRight size={16} />
            </Link>
            <Link data-testid="selling-tips-cta-contact" to="/#contact" className="rounded-full border border-[#E890A7]/40 text-[#F2A7BA] px-7 py-3.5 flex items-center gap-2 hover:bg-[#E890A7]/10 transition-all duration-300">
              Talk to Joselle <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
